import { inject, Injectable } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';
import { MenuService } from './menu.service';
import { Menu } from '@app/share/type/menu';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NotFoundComponent } from '@app/routes/notFound/notFound.component';
import { AuthService } from './auth.service';
import { HTTP_STATUS, MENU_CACHE_KEY, ROUTE_CACHE_KEY } from '@app/common/constants';
import { Location } from '@angular/common';
import { componentMap } from '@app/component-map';
import { canActivateWhatsappEmbeddedSignup } from '@app/common/whatsapp-embedded-signup.guard';

interface CachedData<T> {
  data: T;
  expireTime: number;
}

type RouteType = 'redirect' | 'component' | 'container';

@Injectable({ providedIn: 'root' })
export class RoutingService {
  private router: Router = inject(Router);
  private menuService: MenuService = inject(MenuService);
  private authService: AuthService = inject(AuthService);
  private location: Location = inject(Location);
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1小时缓存
  private menusSubject = new BehaviorSubject<Menu[]>([]);
  public menus$ = this.menusSubject.asObservable();
  private baseRoutes: Routes = [
    {
      path: '**',
      component: NotFoundComponent,
    }
  ];

  constructor() {
    // this.router.events.subscribe(event => console.debug('Router event:', event));
  }

  async initOnAppStart() {
    // const cachedMenus = this.getCachedMenus();
    // const cachedDynamicRoutes = this.getCachedDynamicRoutes();
    // if (cachedMenus && cachedDynamicRoutes && cachedDynamicRoutes.length > 0) {
    //   const menusWithFullPaths = this.buildFullPaths(cachedMenus);
    //   const firstRoute = cachedDynamicRoutes[0]?.path || '404';
    //   const allRoutes: Routes = [
    //     { path: '', redirectTo: firstRoute, pathMatch: 'full' },
    //     ...cachedDynamicRoutes,
    //     ...this.baseRoutes
    //   ];
    //   console.debug('Restored routes from cache:', JSON.stringify(allRoutes, null, 2));
    //   this.router.resetConfig(allRoutes);
    //   this.menusSubject.next(menusWithFullPaths);
    //   await this.router.navigateByUrl(this.location.path() || firstRoute, { replaceUrl: true });
    //   return;
    // }

    await this.generateRoutes();
  }

  async generateRoutes(): Promise<Routes | undefined> {
    if (!this.authService.isLoggedIn()) {
      window.location.href = '/dashboard/#/login';
      return;
    }

    try {
      const menus = await firstValueFrom(this.menuService.getAuthorizedMenus());
      // console.log('Menus:', JSON.stringify(menus, null, 2));
      if (!menus || menus.code !== HTTP_STATUS.SUCCESS || !menus.data || menus.data.length === 0) {
        this.router.resetConfig(this.baseRoutes);
        this.menusSubject.next([]);
        return this.baseRoutes;
      }
      const data = menus.data.filter(item => (item.path && item.component) || item.isFrame == '0' || item.menuType == 'W')
      const menusWithFullPaths = this.buildFullPaths(data);
      const dynamicRoutes = this.convertMenusToRoutes(menusWithFullPaths);
      const firstRoute = dynamicRoutes[0]?.path || '404';

      const allRoutes: Routes = [
        { path: '', redirectTo: firstRoute, pathMatch: 'full' },
        ...dynamicRoutes,
        ...this.baseRoutes
      ];
      // console.log('All routes:', JSON.stringify(allRoutes, null, 2));

      const currentPath = this.location.path();
      this.router.resetConfig(allRoutes);
      await this.router.navigateByUrl(currentPath || firstRoute, { replaceUrl: true });

      this.cacheData(menusWithFullPaths, dynamicRoutes);
      this.menusSubject.next(menusWithFullPaths);

      return allRoutes;
    } catch (error) {
      console.error('ERR002: Failed to generate routes:', error);
      this.router.resetConfig(this.baseRoutes);
      this.menusSubject.next([]);
      return this.baseRoutes;
    }
  }

  private convertMenusToRoutes(menus: Menu[]): Routes {
    return menus.map(menu => {
      if (menu.isFrame === '0' || menu.menuType == 'F') {
        return {};
      }
      const route: Route = {
        path: menu.path,
      };
      if (menu.component) {
        const componentPath = menu.component.replace(/\.ts$/, '');
        // console.log('Processing menu:', JSON.stringify(menu, null, 2));
        if (!componentPath) {
          console.error(`ERR003: Invalid component path for menu: ${JSON.stringify(menu)}`);
        } else if (componentMap[componentPath]) {
          route.loadComponent = componentMap[componentPath];
          if (componentPath === 'embeddedSignup/whatsappEmbeddedSignup/whatsappEmbeddedSignup.component') {
            route.canActivate = [canActivateWhatsappEmbeddedSignup];
          }
        } else {
          console.error(`ERR004: No static mapping found for component: ${componentPath}`);
          route.loadComponent = () => Promise.reject(`Component ${componentPath} not found in componentMap`);
        }
      }

      if (menu.children && menu.children.length > 0) {
        const childRoutes = this.convertMenusToRoutes(menu.children);
        const defaultChild = menu.children.find(child => child.defaultChild) || menu.children[0];
        const defaultChildPath = defaultChild?.path || '';
        route.children = [
          {
            path: '',
            redirectTo: defaultChildPath,
            pathMatch: 'full'
          },
          ...childRoutes
        ];
      }

      return route;
    }).filter(route => route.hasOwnProperty('path'));
  }

  private buildFullPaths(menus: Menu[], parentPath: string = ''): Menu[] {
    return menus.map(menu => {
      menu.fullPath = parentPath ? `${parentPath}/${menu.path}` : menu.path;
      if (menu.children) {
        menu.children = this.buildFullPaths(menu.children, menu.fullPath);
      }
      return menu;
    });
  }

  clearCache() {
    localStorage.removeItem(MENU_CACHE_KEY);
    localStorage.removeItem(ROUTE_CACHE_KEY);
    this.menusSubject.next([]);
  }

  private cacheData(menus: Menu[], dynamicRoutes: Routes) {
    const expireTime = Date.now() + this.CACHE_DURATION;
    const menuCache: CachedData<Menu[]> = { data: menus, expireTime };
    try {
      localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(menuCache));
    } catch (error) {
      console.error('ERR005: Failed to cache menus:', error);
    }

    const menuMap = this.buildMenuMap(menus);
    const serializableRoutes = this.serializeRoutes(dynamicRoutes, menuMap);
    const routeCache: CachedData<any[]> = { data: serializableRoutes, expireTime };
    try {
      localStorage.setItem(ROUTE_CACHE_KEY, JSON.stringify(routeCache));
    } catch (error) {
      console.error('ERR006: Failed to cache dynamic routes:', error);
    }
  }

  private buildMenuMap(menus: Menu[]): Map<string, Menu> {
    const menuMap = new Map<string, Menu>();
    const collectMenus = (menuArray: Menu[], parentPath: string = '') => {
      menuArray.forEach(menu => {
        if (menu.path) {
          const fullPath = parentPath ? `${parentPath}/${menu.path}` : menu.path;
          menuMap.set(fullPath, menu);
          if (menu.children) {
            collectMenus(menu.children, fullPath);
          }
        }
      });
    };
    collectMenus(menus);
    return menuMap;
  }

  private serializeRoutes(routes: Routes, menuMap: Map<string, Menu>, parentPath: string = ''): any[] {
    return routes.map(route => {
      if (route.redirectTo) {
        return {
          path: route.path,
          redirectTo: route.redirectTo,
          pathMatch: route.pathMatch,
          type: 'redirect' as RouteType
        };
      }

      if (route.loadComponent) {
        const fullPath = parentPath ? `${parentPath}/${route.path}` : route.path || '';
        const menu = menuMap.get(fullPath);
        const componentPath = menu?.component ? menu.component.replace(/\.ts$/, '') : '';
        if (!componentPath) {
          console.warn(`ERR007: No component path found for route: ${fullPath}`);
        }
        return {
          path: route.path,
          componentPath,
          children: route.children ? this.serializeRoutes(route.children, menuMap, fullPath) : [],
          type: 'component' as RouteType
        };
      }

      if (route.children) {
        const fullPath = parentPath ? `${parentPath}/${route.path}` : route.path;
        return {
          path: route.path,
          children: this.serializeRoutes(route.children, menuMap, fullPath),
          type: 'container' as RouteType
        };
      }

      return { path: route.path };
    }).filter(route => route.path !== undefined);
  }

  private getCachedMenus(): Menu[] | null {
    try {
      const cached = localStorage.getItem(MENU_CACHE_KEY);
      if (!cached) {
        return null;
      }

      const { data, expireTime } = JSON.parse(cached) as CachedData<Menu[]>;
      if (Date.now() >= expireTime) {
        return null;
      }
      if (!Array.isArray(data) || data.some(menu => !menu.path)) {
        return null;
      }
      return data;
    } catch (error) {
      console.error('ERR009: Failed to retrieve cached menus:', error);
      return null;
    }
  }

  private getCachedDynamicRoutes(): Routes | null {
    try {
      const cached = localStorage.getItem(ROUTE_CACHE_KEY);
      if (!cached) {
        return null;
      }

      const { data, expireTime } = JSON.parse(cached) as CachedData<any[]>;
      if (Date.now() >= expireTime) {
        return null;
      }
      const deserialized = this.deserializeRoutes(data);
      if (deserialized.length === 0) {
        return null;
      }
      return deserialized;
    } catch (error) {
      console.error('ERR011: Failed to retrieve cached dynamic routes:', error);
      return null;
    }
  }

  private deserializeRoutes(serializedRoutes: any[]): Routes {
    return serializedRoutes.map(serialized => {
      switch (serialized.type) {
        case 'redirect':
          return {
            path: serialized.path,
            redirectTo: serialized.redirectTo,
            pathMatch: serialized.pathMatch
          };
        case 'component':
          const componentPath = serialized.componentPath;
          if (!componentPath) {
            return { path: serialized.path };
          }
          const route: Route = {
            path: serialized.path,
            loadComponent: componentMap[componentPath] || (() => Promise.reject(`No static mapping for ${componentPath}`)),
            children: serialized.children ? this.deserializeRoutes(serialized.children) : []
          };
          if (componentPath === 'embeddedSignup/whatsappEmbeddedSignup/whatsappEmbeddedSignup.component') {
            route.canActivate = [canActivateWhatsappEmbeddedSignup];
          }
          return route;
        case 'container':
          return {
            path: serialized.path,
            children: serialized.children ? this.deserializeRoutes(serialized.children) : []
          };
        default:
          return { path: serialized.path };
      }
    }).filter(route => route.path !== undefined);
  }
}